import { Wrench, Shield, Award, Clock, Sparkles, FileCheck } from "lucide-react";
import useContent from "../lib/useContent";

const ICONS = { wrench: Wrench, shield: Shield, award: Award, clock: Clock, sparkles: Sparkles, fileCheck: FileCheck };

const DEFAULT_FEATURES = [
  { icon: "wrench", label: "Swiss Tools" },
  { icon: "shield", label: "Authentic Parts" },
  { icon: "award", label: "Master Watchmakers" },
  { icon: "fileCheck", label: "2-Year Warranty" },
  { icon: "clock", label: "Quick Turnaround" },
  { icon: "sparkles", label: "Free Diagnostics" },
];

const DEFAULT = {
  overline: "Expert Watch Maintenance",
  title: "Professional Watch Services.",
  subtitle:
    "Restore the precision and beauty of your luxury timepiece. Our certified master watchmakers provide comprehensive care using authentic parts and specialized Swiss instruments.",
  cta_label: "Book a Service",
  cta_href: "/contact",
  hero_image: "https://images.unsplash.com/photo-1495856458515-0637185db551?w=1400",
  hero_alt: "Master watchmaker",
  items: DEFAULT_FEATURES,
};

export default function ServicesSection() {
  const { data } = useContent("homepage_services", DEFAULT);
  const overline = data?.overline || DEFAULT.overline;
  const title = data?.title || DEFAULT.title;
  const subtitle = data?.subtitle || DEFAULT.subtitle;
  const ctaLabel = data?.cta_label || DEFAULT.cta_label;
  const ctaHref = data?.cta_href || DEFAULT.cta_href;
  const heroImage = data?.hero_image || DEFAULT.hero_image;
  const heroAlt = data?.hero_alt || DEFAULT.hero_alt;
  const items = (data?.items?.length ? data.items : DEFAULT_FEATURES).map((it) => ({
    icon: it.icon || "sparkles",
    label: it.label || "",
  }));

  // Highlight the part after first space in title (matches original "Watch Services." accent)
  const [first, ...rest] = (title || "").split(" ");
  const accent = rest.join(" ");

  return (
    <section className="bg-[#FAF7F2] border-y border-black/5">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-24 grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div>
          <div className="overline mb-4">{overline}</div>
          <h2 className="font-display text-4xl md:text-5xl text-dark tracking-tight leading-[1.05]">
            {first}{accent && <> <em className="text-copper not-italic">{accent}</em></>}
          </h2>
          <p className="text-gray-600 mt-6 leading-relaxed">{subtitle}</p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 mt-10">
            {items.map((f, i) => {
              const Icon = ICONS[f.icon] || Sparkles;
              return (
                <div key={i} className="flex items-center gap-3" data-testid={`service-feature-${i}`}>
                  <Icon size={16} className="text-copper"/><span className="text-sm text-gray-800">{f.label}</span>
                </div>
              );
            })}
          </div>
          {ctaLabel && <a href={ctaHref} data-testid="services-cta" className="btn-primary mt-10">{ctaLabel}</a>}
        </div>
        <div className="aspect-[4/5] overflow-hidden bg-[#EAE3D5]">
          <img src={heroImage} alt={heroAlt} loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = DEFAULT.hero_image; }}/>
        </div>
      </div>
    </section>
  );
}
